'use client';

// The page a client opens from an Instagram invite (hub de clientes, item 4),
// with no Crie3 login: who invited them, one button to Instagram (through
// Zernio), and where they stand when they come back. AuthGate renders it in
// place of the app, signed in or not — the link is for someone else.

import { useCallback, useEffect, useState } from 'react';

import { Button } from '@open-design/components';
import type {
  PublicInstagramInviteConnectResponse,
  PublicInstagramInviteResponse,
} from '@open-design/contracts';

import { useI18n } from '../i18n';
import type { Dict } from '../i18n/types';
import styles from './InstagramInviteView.module.css';
import { Crie3Wordmark } from '../components/Crie3Wordmark';

interface InstagramInviteViewProps {
  token: string;
  /** The page's query string; the way back from Zernio says how it went. */
  search: string;
  /** Sends the browser to Zernio. A prop so tests need no real navigation. */
  navigate?: (url: string) => void;
}

type Load =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'failed' }
  | { kind: 'ready'; invite: PublicInstagramInviteResponse };

/** What a failed return from Zernio says, by the reason the daemon gave. */
function returnErrorKey(reason: string | null): keyof Dict {
  if (reason === 'denied') return 'invite.errorDenied';
  if (reason === 'account_taken') return 'invite.errorTaken';
  return 'invite.errorGeneric';
}

export function InstagramInviteView({
  token,
  search,
  navigate = (url) => window.location.assign(url),
}: InstagramInviteViewProps) {
  const { t } = useI18n();
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [busy, setBusy] = useState(false);
  const [connectError, setConnectError] = useState<keyof Dict | null>(null);
  const params = new URLSearchParams(search);
  const returnedWithError = params.get('status') === 'error';
  const apiPath = `/api/public/instagram-invites/${encodeURIComponent(token)}`;

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const res = await fetch(apiPath);
        if (!alive) return;
        if (res.status === 404) return setLoad({ kind: 'missing' });
        if (!res.ok) return setLoad({ kind: 'failed' });
        setLoad({ kind: 'ready', invite: (await res.json()) as PublicInstagramInviteResponse });
      } catch {
        if (alive) setLoad({ kind: 'failed' });
      }
    })();
    return () => {
      alive = false;
    };
  }, [apiPath]);

  const connect = useCallback(async () => {
    setBusy(true);
    setConnectError(null);
    try {
      const res = await fetch(`${apiPath}/connect`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: '{}',
      });
      if (!res.ok) {
        setConnectError('invite.errorGeneric');
        return;
      }
      const { url } = (await res.json()) as PublicInstagramInviteConnectResponse;
      navigate(url);
    } catch {
      setConnectError('invite.errorGeneric');
    } finally {
      setBusy(false);
    }
  }, [apiPath, navigate]);

  let content;
  if (load.kind === 'loading') {
    content = <p className={styles.hint}>{t('invite.loading')}</p>;
  } else if (load.kind === 'missing') {
    content = <p className={styles.error}>{t('invite.notFound')}</p>;
  } else if (load.kind === 'failed') {
    content = <p className={styles.error}>{t('invite.loadError')}</p>;
  } else {
    const { invite } = load;
    const names = { inviter: invite.inviterName ?? t('invite.whoSent'), client: invite.clientName };
    if (invite.state === 'used') {
      content = (
        <p className={styles.done}>
          {invite.username
            ? t('invite.connected', { username: invite.username, client: invite.clientName })
            : t('invite.connectedNoName', { client: invite.clientName })}
        </p>
      );
    } else if (invite.state === 'expired' || invite.state === 'revoked') {
      content = (
        <p className={styles.error}>{t(invite.state === 'expired' ? 'invite.expired' : 'invite.revoked', names)}</p>
      );
    } else {
      const errorKey = connectError ?? (returnedWithError ? returnErrorKey(params.get('reason')) : null);
      content = (
        <>
          <p className={styles.body}>
            {invite.inviterName ? t('invite.body', names) : t('invite.bodyNoInviter', names)}
          </p>
          {errorKey ? <p className={styles.error}>{t(errorKey, names)}</p> : null}
          <Button type="button" variant="primary" disabled={busy} onClick={() => void connect()}>
            {t('invite.connect')}
          </Button>
          <p className={styles.hint}>{t('invite.privacy')}</p>
        </>
      );
    }
  }

  return (
    <main className={styles.page}>
      <section className={styles.card}>
        <span className={styles.brand}><Crie3Wordmark height="24px" /></span>
        <h1 className={styles.title}>{t('invite.title')}</h1>
        {content}
      </section>
    </main>
  );
}
